import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, MapPin, Users, IndianRupee, Globe } from "lucide-react";
import api from "@/lib/api";
import { optimizeImageUrl } from "@/lib/imageUtils";
import PageBanner from "@/components/PageBanner";

export default function Vacancies() {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageEnabled, setPageEnabled] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/vacancies").then((res) => setVacancies(res.data)).catch(() => {}),
      api.get("/page-visibility").then((res) => setPageEnabled(res.data.vacancies)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (!loading && !pageEnabled) {
    return (
      <div data-testid="vacancies-page-disabled" className="min-h-[60vh] flex items-center justify-center text-[var(--brand-secondary)]">
        This page is currently unavailable.
      </div>
    );
  }

  return (
    <div data-testid="vacancies-list-page" className="min-h-[70vh] bg-[#FDFCFB]">
      <PageBanner pageKey="vacancies" eyebrow="Work Abroad Opportunities" title="Work Visa Vacancies" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 pt-10 sm:pt-0">
        <p data-testid="vacancies-seo-intro" className="text-center text-sm text-[var(--brand-secondary)] max-w-3xl mx-auto mb-4 leading-relaxed">
          Find genuine work visa vacancies in Saudi Arabia, UAE, Qatar, Kuwait, Bahrain and Oman. Gulfudaan verifies every listing and supports you through the complete process - GAMCA medical appointment, visa stamping, documentation and travel - so you can take up your Gulf job opportunity without agents or middlemen.
        </p>
        <p data-testid="vacancies-availability-note" className="text-center text-xs text-[var(--brand-secondary)] mb-8 flex items-center justify-center gap-1.5">
          <Globe size={13} className="text-[var(--brand-primary)]" /> Currently available for applicants from <strong className="text-[#1A1A1A]">India</strong>
        </p>
        {loading ? (
          <div className="flex justify-center"><Loader2 className="animate-spin text-[var(--brand-primary)]" size={28} /></div>
        ) : vacancies.length === 0 ? (
          <p data-testid="vacancies-empty-state" className="text-center text-[var(--brand-secondary)] py-10">No vacancies available right now. Please check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {vacancies.map((v) => (
              <Link key={v.slug} to={`/vacancies/${v.slug}`} data-testid={`vacancy-card-${v.slug}`}
                className="rounded-lg border border-[#E5E1DA] bg-white overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                {v.banner_image && (
                  <div className="relative">
                    <img src={optimizeImageUrl(v.banner_image, 500)} alt={v.title} loading="lazy" className="w-full h-44 object-cover" />
                    <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wide font-bold bg-[var(--brand-primary)] text-white px-2.5 py-1 rounded-full flex items-center gap-1"><MapPin size={10} /> {v.country_of_work}</span>
                  </div>
                )}
                <div className="p-6">
                  <h3 className="font-heading text-xl text-[#1A1A1A] mb-2 line-clamp-2">{v.title}</h3>
                  {v.trade && <p className="text-xs text-[var(--brand-secondary)] mb-2">{v.trade}</p>}
                  <div className="space-y-1.5 text-sm text-[var(--brand-secondary)]">
                    {v.salary && <p className="flex items-center gap-1.5"><IndianRupee size={13} className="text-[var(--brand-primary)]" /> {v.salary}</p>}
                    <p className="flex items-center gap-1.5"><Users size={13} className="text-[var(--brand-primary)]" /> {v.quantity} Position{v.quantity > 1 ? "s" : ""}</p>
                  </div>
                  <span className="text-[var(--brand-primary)] text-sm font-medium flex items-center gap-1 mt-4">View & Apply <ArrowRight size={14} /></span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
